/* casa-free-gift.js — free-gift presence automation (Mini Sunset Orb promo).
 *
 * THREE MECHANISMS:
 *  1. Fetch shim (first-paint path): a POST to /cart/add(.js) whose payload
 *     contains a qualifying variant gets the gift appended to the SAME
 *     request, so the drawer's first render already includes it — no pop-in,
 *     no second round-trip. Buy-box adds are FormData (product-form.js), so
 *     the shim converts FormData→JSON faithfully before appending.
 *  2. Reconciler (convergence net): on every `cart:update` it reads /cart.js
 *     and converges the gift's quantity — removes it when the last qualifying
 *     line leaves (or when it was duplicated/quantity-tampered), adds it when
 *     a qualifying line arrived via a path the shim doesn't know (quick-add,
 *     drawer upsell).
 *  3. Express bridge: the product-page accelerated checkout button (Shop Pay,
 *     Apple Pay, Buy it now) skips the cart entirely, so for a gift-eligible
 *     variant its click is redirected to a cart permalink of gift + item
 *     (Shop Pay keeps `payment=shop_pay`). See "express-checkout bridge".
 *
 * INTEGRATION CONTRACTS (see the 2026-08-25 architecture review):
 *  - Loads AFTER casa-edd.js (theme.liquid order): page fetch → THIS shim →
 *    EDD's shim → network. EDD then stamps its canonical delivery property on
 *    the gift item too, which keeps a gift line distinct from any PAID line
 *    of the same variant (`_free_gift` splits them regardless).
 *  - Writes are flush-aware: awaits flushPendingCartUpdates() on every
 *    cart-items-component before touching the cart (the product-form.js
 *    pattern), so we never race the drawer's serial edit queue.
 *  - This file NEVER morphs the DOM. It dispatches a `cart:update`
 *    CustomEvent carrying the re-rendered `sections`; cart-items-component's
 *    own handler morphs with its optimistic-state protections intact. Its
 *    source ('casa-free-gift') is deliberately NOT in cart-drawer's
 *    auto-open allowlist.
 *  - Market gate: the gift is only ADDED when the storefront country (script
 *    tag data attr, server-rendered) is in the allowlist. REMOVAL runs in
 *    every market, so a market-switched session still cleans up.
 *  - Theme editor: hard bail in designMode — never mutate a preview cart.
 *
 * CONFIG IS DATA, NEVER CODE: the qualifying-variant → gift-variant mapping
 * comes from (a) the PDP's casa-fg-data JSON (blocks/variant-picker.liquid)
 * and (b) data-casa-fg-qualifying attributes on rendered cart rows
 * (cart-products.liquid + casa-cart-items.liquid). No product ids appear in
 * this file. Mapping is cached in sessionStorage so a quick-add from a
 * collection page still resolves after one render cycle.
 */
(function () {
  'use strict';
  if (window.__casaFreeGiftInstalled) return;
  window.__casaFreeGiftInstalled = true;
  if (window.Shopify && window.Shopify.designMode) return;

  var GIFT_PROP = '_free_gift';
  var CACHE_KEY = 'casa-fg-map-v1';
  var script = document.currentScript;
  var CHOICE_ATTR = '_casa_free_gift_choice';
  var giftChoice = (script && script.getAttribute('data-casa-fg-choice')) || '';
  var COUNTRY = (script && script.getAttribute('data-casa-fg-country')) || (window.Shopify && window.Shopify.country) || '';
  var ALLOW = ((script && script.getAttribute('data-casa-fg-allow')) || 'US').toUpperCase().split(',');
  var marketAllowed = ALLOW.indexOf(String(COUNTRY).toUpperCase()) !== -1;

  // ---- $0 gift product override (theme setting casa_fg_gift_product) ---------
  // When set, every mirror whose metafield names the paid Remote Orb gets the
  // $0 unlisted gift product instead: ONE per qualifying mirror unit (Elie,
  // Oct 10: "1 free orb PER large mirror (any mirror)"), never more than the
  // paid Remote Orb's sellable stock (data-casa-fg-stock). No discount
  // touches it, so no code, sale or upsell can charge it or knock it out.
  //  - swap: paid Remote Orbs already in the cart turn into gifts up to the
  //    gift count (_free_gift_swapped marks them); extras stay paid;
  //  - revert: when the gift count drops (mirror removed, US market left)
  //    swapped gifts go back to paid Remote Orbs, count restored exactly;
  //  - spend windows (BFCM): a pre-discount total at or above the threshold
  //    inside a UTC window qualifies too (gift lines excluded, a swapped gift
  //    counted at its paid price), matching snippets/casa-fg-config.liquid.
  //    It gives one Orb to a cart with no mirror; mirrors still give one
  //    each, with no extra Orb on top (Elie, Oct 10).
  function attr(name) { return (script && script.getAttribute(name)) || ''; }
  var SWAP_PROP = '_free_gift_swapped';
  var OVERRIDE = attr('data-casa-fg-gift');
  var OVERRIDE_AVAILABLE = attr('data-casa-fg-gift-available') !== 'false';
  var PAID = attr('data-casa-fg-paid');
  var PAID_PRICE = parseInt(attr('data-casa-fg-paid-price'), 10) || 0;
  var SPEND = parseInt(attr('data-casa-fg-spend'), 10) || 0;
  var WINDOWS = attr('data-casa-fg-windows').split(',').map(function (w) {
    var pair = w.trim().split('/');
    return pair.length === 2 ? [Date.parse(pair[0]), Date.parse(pair[1])] : null;
  }).filter(function (w) { return w && !isNaN(w[0]) && !isNaN(w[1]); });
  var lastCart = null; // {hasGift, hasPaid} from the reconciler's last read

  // Read live (not cached) so a stock change re-rendered into the tag counts.
  function giftStockCap() {
    var raw = script && script.getAttribute('data-casa-fg-stock');
    if (raw == null || raw === '') return Infinity; // untracked: no cap
    var n = parseInt(raw, 10);
    return isNaN(n) ? Infinity : Math.max(0, n);
  }

  function spendWindowOpen() {
    var now = Date.now();
    return WINDOWS.some(function (w) { return now >= w[0] && now <= w[1]; });
  }

  function spendQualifies(items) {
    if (!OVERRIDE || !SPEND || !spendWindowOpen()) return false;
    var base = 0;
    items.forEach(function (it) {
      var props = it.properties || {};
      if (!props[GIFT_PROP]) base += it.original_line_price || 0;
      else if (props[SWAP_PROP]) base += PAID_PRICE;
    });
    return base >= SPEND;
  }

  // Ajax endpoints, each ending in exactly one `.js`. Theme.routes is not
  // uniform: scripts.liquid already appends `.js` to cart_add_url but not to
  // the others, so a bare `+ '.js'` would post the gift add to /cart/add.js.js
  // (404, swallowed). Today `Theme` is a top-level const, not a window
  // property, so the fallbacks apply; this keeps the add working if the routes
  // are ever exposed on window (6 Oct audit).
  var routes = (window.Theme && window.Theme.routes) || {};
  function ajaxUrl(route, fallback) {
    var url = typeof route === 'string' && route ? route : fallback;
    return url.replace(/\/+$/, '').replace(/(\.js)+$/, '') + '.js';
  }
  var ADD_JS = ajaxUrl(routes.cart_add_url, '/cart/add');
  var CHANGE_JS = ajaxUrl(routes.cart_change_url, '/cart/change');
  var CART_URL = routes.cart_url || '/cart';
  var CART_JS = ajaxUrl(CART_URL, '/cart');
  var UPDATE_JS = ajaxUrl(routes.cart_update_url, '/cart/update');

  // ---- qualifying-variant → {gift, available} mapping ----------------------
  var map = {};
  try {
    var stored = sessionStorage.getItem(CACHE_KEY);
    if (stored) map = JSON.parse(stored) || {};
  } catch (e) {}

  function rememberMapping(variantId, giftId, available) {
    if (!variantId || !giftId) return;
    var k = String(variantId);
    var next = { gift: String(giftId), available: available !== false };
    var prev = map[k];
    if (prev && prev.gift === next.gift && prev.available === next.available) return;
    map[k] = next;
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(map));
    } catch (e) {}
  }

  function harvestMappings() {
    var before = JSON.stringify(map);
    // (a) PDP payloads — blocks/variant-picker.liquid ships giftVariantId per qualifying variant
    var blocks = document.querySelectorAll('script[id^="casa-fg-data-"]');
    for (var i = 0; i < blocks.length; i++) {
      try {
        var data = JSON.parse(blocks[i].textContent);
        var variants = (data && data.variants) || {};
        for (var vid in variants) {
          if (!Object.prototype.hasOwnProperty.call(variants, vid)) continue;
          var entry = variants[vid];
          if (entry && entry.giftVariantId) rememberMapping(vid, entry.giftVariantId, entry.giftAvailable);
        }
      } catch (e) {}
    }
    // (b) rendered cart rows — both renderers stamp qualifying lines
    var rows = document.querySelectorAll('[data-casa-fg-qualifying]');
    for (var j = 0; j < rows.length; j++) {
      var row = rows[j];
      rememberMapping(
        row.getAttribute('data-variant-id') || rowVariantFromKey(row),
        row.getAttribute('data-casa-fg-qualifying'),
        row.getAttribute('data-casa-fg-available') !== 'false'
      );
    }
    return JSON.stringify(map) !== before;
  }

  function rowVariantFromKey(row) {
    // drawer rows carry only data-key ("<variantId>:<hash>")
    var key = row.getAttribute('data-key') || '';
    var idx = key.indexOf(':');
    return idx > 0 ? key.slice(0, idx) : null;
  }

  function mappingFor(variantId) {
    var entry = map[String(variantId)] || null;
    if (entry && OVERRIDE && (!PAID || entry.gift === String(PAID))) {
      return { gift: OVERRIDE, available: OVERRIDE_AVAILABLE && entry.available };
    }
    return entry;
  }

  // ---- fetch shim: same-request gift injection -----------------------------
  var nativeFetch = window.fetch;

  function isAddUrl(url) {
    try {
      var path = new URL(url, window.location.href).pathname.replace(/\/+$/, '');
      return /\/cart\/add(\.js)?$/.test(path);
    } catch (e) {
      return false;
    }
  }

  function formToJson(fd) {
    // Faithful FormData→JSON for /cart/add: one item + top-level sections.
    // Only the keys /cart/add consumes are mapped; anything else is dropped
    // deliberately (form_type/utf8/etc. are form-transport noise).
    var item = { quantity: 1, properties: {} };
    var body = { items: [item] };
    var hasId = false;
    fd.forEach(function (value, key) {
      if (key === 'id') {
        item.id = value;
        hasId = true;
      } else if (key === 'quantity') {
        var q = parseInt(value, 10);
        if (!isNaN(q) && q > 0) item.quantity = q;
      } else if (key === 'selling_plan' && value) {
        item.selling_plan = value;
      } else if (key === 'sections') {
        body.sections = value;
      } else if (key === 'sections_url') {
        body.sections_url = value;
      } else {
        var m = /^properties\[(.+)\]$/.exec(key);
        if (m) item.properties[m[1]] = value;
      }
    });
    if (!hasId) return null;
    if (Object.keys(item.properties).length === 0) delete item.properties;
    return body;
  }

  function giftFromItems(items, requireAvailable) {
    // All eligible mirror sizes in this promotion map to the same orb.
    // Count units, including separate lines of the same qualifying variant.
    var gift = null;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || (item.properties && item.properties[GIFT_PROP])) continue;
      var entry = mappingFor(item.variant_id != null ? item.variant_id : item.id);
      if (!entry || (requireAvailable && !entry.available)) continue;
      if (!gift) gift = { id: entry.gift, quantity: 0, available: entry.available };
      if (entry.gift === gift.id) gift.quantity += Math.max(0, parseInt(item.quantity == null ? 1 : item.quantity, 10) || 0);
    }
    if (gift && OVERRIDE && gift.id === OVERRIDE) gift.quantity = Math.min(gift.quantity, giftStockCap());
    return gift && gift.quantity > 0 ? gift : null;
  }

  function payloadHasGift(items, variantId) {
    return items.some(function (item) {
      return item && String(item.id) === String(variantId) && item.properties && item.properties[GIFT_PROP];
    });
  }

  function injectGift(input, init) {
    // Returns a replacement [input, init] or null to pass through untouched.
    if (!marketAllowed || giftGaveUp || giftChoice) return null;
    harvestMappings();
    var body = init && init.body;
    var parsed = null;

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      parsed = formToJson(body);
    } else if (typeof body === 'string' && body) {
      try {
        var p = JSON.parse(body);
        if (p && (Array.isArray(p.items) || p.id != null)) {
          parsed = Array.isArray(p.items) ? p : { items: [p] };
          if (!Array.isArray(p.items)) {
            // preserve any top-level sections on a bare {id,quantity} body
            if (p.sections) parsed.sections = p.sections;
            if (p.sections_url) parsed.sections_url = p.sections_url;
          }
        }
      } catch (e) {
        parsed = null;
      }
    }
    if (!parsed || !Array.isArray(parsed.items) || !parsed.items.length) return null;

    var gift = giftFromItems(parsed.items, true);
    if (!gift) return null;
    if (OVERRIDE && gift.id === OVERRIDE) {
      // The cart already holds a gift or a paid Remote Orb (or this add brings
      // one): leave it to the reconciler, which caps or swaps.
      if (lastCart && (lastCart.hasGift || lastCart.hasPaid)) return null;
      if (PAID && parsed.items.some(function (it) { return it && String(it.id) === String(PAID); })) return null;
    }
    if (payloadHasGift(parsed.items, gift.id)) return null; // already there (incl. our own reconciler add)

    var giftItem = { id: gift.id, quantity: gift.quantity, properties: {} };
    giftItem.properties[GIFT_PROP] = '1';
    parsed.items.push(giftItem);

    var headers = Object.assign({}, (init && init.headers) || {});
    headers['Content-Type'] = 'application/json';
    return [input, Object.assign({}, init, { body: JSON.stringify(parsed), headers: headers })];
  }

  function isCartWriteUrl(url) {
    try {
      var path = new URL(url, window.location.href).pathname.replace(/\/+$/, '');
      return /\/cart\/(add|change|update|clear)(\.js)?$/.test(path);
    } catch (e) {
      return false;
    }
  }

  function noteCart(items) {
    var gift = false;
    var paid = false;
    (items || []).forEach(function (it) {
      if (String(it.variant_id) === OVERRIDE || (it.properties || {})[GIFT_PROP]) gift = true;
      else if (PAID && String(it.variant_id) === String(PAID)) paid = true;
    });
    lastCart = { hasGift: gift, hasPaid: paid };
  }

  window.fetch = function (input, init) {
    try {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : '';
      var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method === 'POST' && isAddUrl(url)) {
        if (OVERRIDE && !lastCart && marketAllowed) {
          // The cart changed since we last looked: read it first, so a paid
          // Orb already there is swapped by the reconciler, not doubled here.
          return nativeFetch(CART_JS, { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) { noteCart(cart.items); }, function () {})
            .then(function () {
              var later = null;
              try { later = injectGift(input, init); } catch (e) {}
              lastCart = null;
              return later ? nativeFetch(later[0], later[1]) : nativeFetch(input, init);
            });
        }
        var replaced = injectGift(input, init);
        lastCart = null;
        if (replaced) return nativeFetch(replaced[0], replaced[1]);
      } else if (method === 'POST' && isCartWriteUrl(url)) {
        lastCart = null;
      }
    } catch (e) {
      /* never break an add on a bug in here */
    }
    return nativeFetch(input, init);
  };

  // ---- reconciler: stateless convergence on cart:update --------------------
  var giftGaveUp = false; // set on a 422 gift add (sold out) — stop for the session
  var busy = false;
  var dirty = false;
  var debounceTimer = null;
  var recheckArmed = false;

  function sectionIds() {
    var ids = [];
    var comps = document.querySelectorAll('cart-items-component');
    for (var i = 0; i < comps.length; i++) {
      var id = comps[i].dataset ? comps[i].dataset.sectionId : null;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    }
    return ids;
  }

  function flushComponents() {
    var comps = document.querySelectorAll('cart-items-component');
    var waits = [];
    for (var i = 0; i < comps.length; i++) {
      var c = comps[i];
      if (c && typeof c.flushPendingCartUpdates === 'function') {
        try {
          waits.push(c.flushPendingCartUpdates());
        } catch (e) {}
      }
    }
    return Promise.all(waits);
  }

  function fetchCart() {
    return nativeFetch(CART_JS, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('Cart read failed');
      return r.json();
    });
  }

  function cartWrite(url, payload) {
    var ids = sectionIds();
    if (ids.length) {
      payload.sections = ids.join(',');
      payload.sections_url = window.location.pathname;
    }
    lastCart = null; // re-read before the next shimmed add
    return nativeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok && r.status !== 422) throw new Error('Gift update failed');
        return { status: r.status, json: json };
      });
    });
  }

  function announce(result) {
    // Hand the re-rendered sections to the theme's own morph pipeline.
    var cart = result && result.json ? result.json : {};
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: 'casa-free-gift',
          data: {
            source: 'casa-free-gift',
            itemCount: typeof cart.item_count === 'number' ? cart.item_count : undefined,
            sections: cart.sections || (result && result.json && result.json.sections) || undefined,
          },
        },
      })
    );
  }

  function choiceWrite(choice, lines) {
    var payload = { attributes: {}, updates: {} };
    payload.attributes[CHOICE_ATTR] = choice;
    (lines || []).forEach(function (line) { payload.updates[line.key] = 0; });
    giftChoice = choice;
    return cartWrite(UPDATE_JS, payload).then(function (result) {
      if (result.status >= 400 || result.json.errors) throw new Error('Gift update rejected');
      announce(result);
      return result;
    });
  }

  function priceChoice(cart) {
    return 'price:' + ((cart && cart.discount_codes) || []).map(function (code) {
      return code.code.toUpperCase();
    }).sort().join(',');
  }

  function reconcile() {
    if (busy) {
      dirty = true;
      return;
    }
    busy = true;
    dirty = false;

    flushComponents()
      .then(fetchCart)
      .then(function (cart) {
        giftChoice = (cart.attributes && cart.attributes[CHOICE_ATTR]) || '';
        var items = (cart && cart.items) || [];
        if (OVERRIDE) return reconcileOverride(cart, items);
        var giftLines = [];
        var qualifying = giftFromItems(items, false);
        var unknownVariants = false;

        for (var i = 0; i < items.length; i++) {
          var it = items[i];
          if (it.properties && it.properties[GIFT_PROP]) {
            giftLines.push(it);
            continue;
          }
          var entry = mappingFor(it.variant_id);
          if (!entry) unknownVariants = true;
        }

        // Removal runs in EVERY market; addition only where allowed.
        if (!qualifying) {
          if (giftChoice) return choiceWrite('', giftLines);
          if (giftLines.length) {
            // remove all gift lines, serially (there is almost always exactly one)
            var chain = Promise.resolve();
            giftLines.forEach(function (line) {
              chain = chain.then(function () {
                return cartWrite(CHANGE_JS, { id: line.key, quantity: 0 });
              });
            });
            return chain.then(function (last) {
              announce(last);
            });
          }
          // Nothing to do — but if the cart holds variants we can't classify
          // yet (quick-add before the drawer morph landed), look again once.
          if (unknownVariants && !recheckArmed) {
            recheckArmed = true;
            setTimeout(function () {
              recheckArmed = false;
              if (harvestMappings()) schedule();
            }, 700);
          }
          return null;
        }

        if (giftChoice.indexOf('price:') === 0 && giftChoice !== priceChoice(cart)) {
          return choiceWrite('', []).then(function () { dirty = true; });
        }

        if (giftChoice === 'removed' && giftLines.length) return choiceWrite('removed', giftLines);
        // Correct an excess before checking price: reducing mirrors can briefly
        // leave an undiscounted extra orb. It is not a coupon opt-out.
        var giftUnits = giftLines.reduce(function (total, line) { return total + line.quantity; }, 0);
        if (giftUnits > qualifying.quantity || giftLines.length > 1) {
          // Shopify may split one variant into discounted and undiscounted lines.
          // Their keys change as allocations change, so change one fresh key per
          // pass. A batch update can target a stale key after its first deletion.
          var excessLine = giftLines.filter(function (line) { return line.final_line_price > 0; })[0] || giftLines[giftLines.length - 1];
          var nextQuantity = giftUnits > qualifying.quantity
            ? Math.max(0, excessLine.quantity - (giftUnits - qualifying.quantity))
            : 0;
          return cartWrite(CHANGE_JS, { id: excessLine.key, quantity: nextQuantity }).then(function (res) {
            if (res.status < 400 && !res.json.errors) announce(res);
          });
        }
        var chargedGift = giftLines.some(function (line) {
          return typeof line.final_line_price === 'number' && line.final_line_price > 0;
        });
        if (chargedGift) return choiceWrite(priceChoice(cart), giftLines);

        // A qualifying line is present.
        if (giftLines.length === 0) {
          if (!marketAllowed || giftGaveUp || giftChoice || !qualifying.available) return null;
          var payload = { items: [{ id: qualifying.id, quantity: qualifying.quantity, properties: {} }] };
          payload.items[0].properties[GIFT_PROP] = '1';
          return cartWrite(ADD_JS, payload).then(function (res) {
            if (res.status === 422) {
              giftGaveUp = true; // sold out / not sellable — the PDP promise keys off availability too
              return;
            }
            announce(res);
          });
        }

        // Bring the existing gift up to the eligible mirror quantity.
        if (giftLines[0].quantity !== qualifying.quantity && marketAllowed && !giftGaveUp && !giftChoice && qualifying.available) {
          return cartWrite(CHANGE_JS, { id: giftLines[0].key, quantity: qualifying.quantity }).then(function (res) {
            if (res.status === 422) {
              giftGaveUp = true;
              return;
            }
            announce(res);
          });
        }
        return null;
      })
      .catch(function (e) {
        /* network hiccup — the next cart:update converges */
      })
      .then(function () {
        busy = false;
        if (dirty) schedule();
      });
  }

  function changeLine(key, quantity) {
    return cartWrite(CHANGE_JS, { id: key, quantity: quantity }).then(function (res) {
      if (res.status < 400 && !res.json.errors) announce(res);
      return res;
    });
  }

  // One write per pass; announce() fires cart:update, and the next pass
  // continues from the fresh cart until nothing is left to do.
  //
  // T = gift count (mirror units, or 1 for an open spend window with no
  // mirror, capped by stock; 0 outside the US). Gifts already in the cart
  // count first, so an Orb added on purpose next to its free one stays paid.
  // Below T, paid Remote Orbs convert first (swapped), then plain gifts fill
  // the rest. Above T, plain gifts go first, then swapped ones revert to paid.
  function sumQty(lines) {
    return lines.reduce(function (total, line) { return total + line.quantity; }, 0);
  }
  function reconcileOverride(cart, items) {
    var plain = [];
    var swapped = [];
    var legacy = [];
    var paidLines = [];
    var unknownVariants = false;
    items.forEach(function (it) {
      var props = it.properties || {};
      if (String(it.variant_id) === OVERRIDE) {
        // A $0 gift line that lost its _free_gift property (cart edits,
        // permalinks) is still the gift.
        (props[SWAP_PROP] ? swapped : plain).push(it);
      } else if (props[GIFT_PROP]) legacy.push(it);
      else if (PAID && String(it.variant_id) === String(PAID)) paidLines.push(it);
      else if (!map[String(it.variant_id)]) unknownVariants = true;
    });
    noteCart(items);

    // Old automatic-discount gift lines (paid Remote Orb + _free_gift) go;
    // the next pass adds the $0 gift if the cart still qualifies.
    if (legacy.length) return changeLine(legacy[0].key, 0);

    var mirrors = giftFromItems(items, false);
    var target = mirrors ? mirrors.quantity : 0;
    var spend = !mirrors && spendQualifies(items);
    if (spend) target = Math.min(1, giftStockCap());
    var available = mirrors ? mirrors.available : OVERRIDE_AVAILABLE;
    if (!marketAllowed || giftGaveUp || !available) target = 0;

    if (!mirrors && !spend && !plain.length && !swapped.length) {
      if (giftChoice) return choiceWrite('', []);
      if (unknownVariants && !recheckArmed) {
        recheckArmed = true;
        setTimeout(function () {
          recheckArmed = false;
          if (harvestMappings()) schedule();
        }, 700);
      }
      return null;
    }

    if (target > 0) {
      // The shopper removed the gift by hand: honour it until they qualify anew.
      if (giftChoice === 'removed') return plain.length || swapped.length ? choiceWrite('removed', plain.concat(swapped)) : null;
      if (giftChoice) return choiceWrite('', []).then(function () { dirty = true; });
    }

    var S = sumQty(swapped);
    var U = sumQty(plain);

    // 1. One line each: merge split gift lines (a swapped extra goes back to
    //    paid; the next pass converts it again if still owed).
    if (plain.length > 1) return changeLine(plain[plain.length - 1].key, 0);
    if (swapped.length > 1) return revertSwapped(swapped[swapped.length - 1], swapped[swapped.length - 1].quantity);
    // 2. Too many: plain gifts leave first, then swapped ones revert to paid.
    if (U + S > target) {
      if (U > 0) return changeLine(plain[0].key, Math.max(0, target - S));
      return revertSwapped(swapped[0], S - target);
    }
    // 3. Too few: paid Remote Orbs in the cart convert first.
    var gap = target - U - S;
    if (gap > 0 && paidLines.length) {
      var paid = paidLines[0];
      var k = Math.min(gap, paid.quantity);
      var swapWrite = swapped.length
        ? cartWrite(CHANGE_JS, { id: swapped[0].key, quantity: S + k })
        : cartWrite(ADD_JS, { items: [{ id: Number(OVERRIDE), quantity: k, properties: giftProps(true) }] });
      return swapWrite.then(function (res) {
        if (res.status === 422 || res.json.errors) {
          giftGaveUp = true;
          return null;
        }
        return changeLine(paid.key, paid.quantity - k);
      });
    }
    // 4. Then plain gifts fill the rest.
    if (gap > 0) {
      var giftWrite = plain.length
        ? cartWrite(CHANGE_JS, { id: plain[0].key, quantity: U + gap })
        : cartWrite(ADD_JS, { items: [{ id: Number(OVERRIDE), quantity: gap, properties: giftProps(false) }] });
      return giftWrite.then(function (res) {
        if (res.status === 422 || res.json.errors) {
          giftGaveUp = true; // not sellable here; the weekly count covers the rest
          return null;
        }
        announce(res);
      });
    }
    return null;
  }

  // Give the shopper back the Orbs they added themselves, then drop the gifts.
  function revertSwapped(line, quantity) {
    return cartWrite(ADD_JS, { items: [{ id: Number(PAID), quantity: quantity }] }).then(function () {
      return changeLine(line.key, line.quantity - quantity);
    });
  }

  function giftProps(swap) {
    var props = {};
    props[GIFT_PROP] = '1';
    if (swap) props[SWAP_PROP] = '1';
    return props;
  }

  function schedule() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 180);
  }

  // ---- express-checkout bridge (product-page accelerated buttons) ----------
  // Shopify's product-page payment button (Shop Pay / Apple Pay / Buy it now,
  // rendered by `form | payment_button`) checks out the form's variant
  // straight from the page, bypassing /cart/add, so neither the shim nor the
  // reconciler ever sees it and the advertised gift is never added. For a
  // qualifying, gift-eligible variant we stop that click (capture phase, before
  // the button's closed shadow DOM handles it) and send the buyer to a cart
  // permalink holding exactly gift + item, which keeps Buy-it-now semantics
  // (only this product; the rest of the shopper's cart is not bought). The
  // automatic discount prices the gift at $0 at checkout; one gift per mirror
  // unit, matching the cart path (giftFromItems). A Shop Pay click adds
  // the documented `payment=shop_pay`, so it stays one tap into Shop Pay; any
  // other wallet lands on the normal checkout, which offers every express
  // wallet. The gift goes first because permalinks carry line properties for
  // the first product only, and `_free_gift` keeps the line recognisable as
  // the gift if the shopper comes back to the store cart. Everything else
  // (other markets, other variants, a removed or price-guarded gift, unknown
  // mapping, selling plans) falls through to Shopify's native button untouched.
  var expressNavigating = false;

  function paymentButtonInPath(path) {
    // Returns { wrapper, wallet } when the event targets the payment button.
    var wallet = '';
    var host = null;
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!node || node.nodeType !== 1) continue;
      var tag = node.tagName;
      if (tag === 'SHOP-PAY-WALLET-BUTTON') wallet = wallet || 'shop_pay';
      else if (/^SHOPIFY-[A-Z-]+-BUTTON$/.test(tag) || tag === 'MORE-PAYMENT-OPTIONS-LINK') wallet = wallet || 'other';
      else if (tag === 'SHOPIFY-ACCELERATED-CHECKOUT') host = host || node;
      if (node.getAttribute('data-shopify') === 'payment-button') {
        if (i === 0) return null; // the bare container, not a button in it
        if (!wallet && host) {
          // Wallet elements hidden in the closed shadow root: a product page
          // shows the one recommended wallet, named in this attribute.
          try {
            var rec = JSON.parse(host.getAttribute('recommended') || 'null');
            if (rec && rec.name === 'shop_pay') wallet = 'shop_pay';
          } catch (e) {}
        }
        return { wrapper: node, wallet: wallet || 'other' };
      }
    }
    return null;
  }

  function base64Url(text) {
    return btoa(unescape(encodeURIComponent(text))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function spendGiftFor(form, variantId, quantity) {
    // The spend-window gift for a Buy-it-now tap: same rules as spendQualifies
    // for a cart holding only this line. The variant's price comes from the
    // casa-fg-prices JSON rendered inside the product form (cents).
    if (!OVERRIDE || !SPEND || !OVERRIDE_AVAILABLE || !spendWindowOpen()) return null;
    if (String(variantId) === OVERRIDE || (PAID && String(variantId) === String(PAID))) return null;
    var priceEl = form.querySelector('script.casa-fg-prices');
    if (!priceEl) return null;
    var prices;
    try { prices = JSON.parse(priceEl.textContent); } catch (e) { return null; }
    var price = prices && parseInt(prices[String(variantId)], 10);
    if (!price || price * quantity < SPEND) return null;
    return { gift: OVERRIDE, available: true, spend: true };
  }

  function expressCheckoutUrl(event) {
    // The permalink to use instead of the native button, or null.
    if (!marketAllowed || giftGaveUp || giftChoice) return null;
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    var hit = paymentButtonInPath(path);
    if (!hit) return null;
    var form = hit.wrapper.closest('form');
    if (!form) return null;
    var fd = new FormData(form);
    var variantId = fd.get('id');
    if (!variantId || fd.get('selling_plan')) return null; // permalinks can't carry selling plans
    var quantity = parseInt(fd.get('quantity'), 10);
    if (isNaN(quantity) || quantity < 1) quantity = 1;
    harvestMappings();
    var entry = mappingFor(variantId);
    if (!entry) {
      // No per-mirror gift for this variant: an open spend window still gives
      // one Orb when this tap alone reaches the threshold (Nov 2 2026: a $700
      // Calini bought through Shop Pay from its page got no Orb because the
      // tap never passed through the cart).
      entry = spendGiftFor(form, variantId, quantity);
    }
    if (!entry || !entry.available || String(entry.gift) === String(variantId)) return null;
    if (OVERRIDE && entry.gift === OVERRIDE && giftStockCap() < 1) return null;
    var props = {};
    props[GIFT_PROP] = '1';
    var url =
      CART_URL.replace(/\/+$/, '') + '/' +
      encodeURIComponent(entry.gift) + ':' + (entry.spend ? 1 : OVERRIDE && entry.gift === OVERRIDE ? Math.min(quantity, giftStockCap()) : quantity) + ',' + encodeURIComponent(variantId) + ':' + quantity +
      '?properties=' + base64Url(JSON.stringify(props));
    if (hit.wallet === 'shop_pay') url += '&payment=shop_pay';
    return url;
  }

  function expressGuard(event) {
    var url;
    try {
      url = expressCheckoutUrl(event);
    } catch (e) {
      return; // on any bug here the native button keeps working
    }
    if (!url) return;
    // Keep the wallet from starting its own sheet on any part of the gesture.
    event.stopImmediatePropagation();
    if (event.type === 'click') {
      event.preventDefault();
      if (expressNavigating) return; // a second tap while the permalink loads
      expressNavigating = true;
      document.dispatchEvent(new CustomEvent('casa:gift-express', { detail: { url: url } }));
      window.location.assign(url);
    }
  }

  ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'mouseup', 'touchend', 'click'].forEach(function (type) {
    window.addEventListener(type, expressGuard, { capture: true, passive: type.indexOf('touch') === 0 });
  });
  window.addEventListener('pageshow', function () { expressNavigating = false; }); // back/forward cache

  document.addEventListener('cart:update', function (event) {
    // Our own announce() also lands here; the run it triggers is a no-op
    // read that confirms convergence, which is exactly the point.
    harvestMappings();
    schedule();
  });
  document.addEventListener('casa:gift-remove', function () { giftChoice = 'removed'; });
  // The Orb bar's "Add it back" (snippets/casa-cart-orb-bar.liquid): forget the removal, then reconcile adds the gift.
  document.addEventListener('casa:gift-restore', function () {
    choiceWrite('', []).then(function () { dirty = true; schedule(); }).catch(function () { schedule(); });
  });
  document.addEventListener('discount:update', function () { schedule(); });

  // Initial pass — covers a cart persisted from a previous session (e.g. the
  // mirror was removed on another device, or the market changed since).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      harvestMappings();
      schedule();
    });
  } else {
    harvestMappings();
    schedule();
  }
})();
